import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { CalendarIcon, RefreshCw, Search, Zap, Clock, Target } from "lucide-react";
import { authFetch } from "@/utils/auth-fetch";
import { useSite } from "@/contexts/SiteContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";

interface SearchMetrics {
  totalSearches: number;
  avgSearchTime: number;
  medianSearchTime: number;
  p90SearchTime: number;
  p99SearchTime: number;
  avgResults: number;
  zeroResultSearches: number;
  searchesByStrategy: {
    semantic_only: number;
    semantic_with_filters: number;
    filters_only: number;
  };
}

interface HourlyData {
  hour: string;
  searches: number;
  avgTime: number;
  p90Time: number;
  cacheHitRate: number;
}

interface PopularSearch {
  query: string;
  count: number;
  avgTime: number;
  avgResults: number;
}

interface TimingBreakdown {
  phase: string;
  avgTime: number;
  percentage: number;
}

const COLORS = {
  primary: "#3b82f6",
  secondary: "#10b981",
  tertiary: "#f59e0b",
  error: "#ef4444",
  muted: "#6b7280",
};

export default function SearchPerformance() {
  const { selectedSite } = useSite();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<SearchMetrics | null>(null);
  const [hourlyData, setHourlyData] = useState<HourlyData[]>([]);
  const [popularSearches, setPopularSearches] = useState<PopularSearch[]>([]);
  const [timingBreakdown, setTimingBreakdown] = useState<TimingBreakdown[]>([]);
  const [timeRange, setTimeRange] = useState("24h");
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedSite) {
      fetchSearchMetrics();
    }
  }, [selectedSite, timeRange]);

  const fetchSearchMetrics = async () => {
    if (!selectedSite) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch overall metrics
      const metricsResponse = await authFetch(
        `/api/search/performance/metrics?siteId=${selectedSite.id}&timeRange=${timeRange}`
      );

      if (!metricsResponse.ok) {
        throw new Error("Failed to fetch search metrics");
      }

      const metricsData = await metricsResponse.json();
      setMetrics(metricsData.metrics);
      setHourlyData(metricsData.hourlyData || []);
      setPopularSearches(metricsData.popularSearches || []);
      setTimingBreakdown(metricsData.timingBreakdown || []);
    } catch (error) {
      console.error("Error fetching search metrics:", error);
      setError("Failed to load search performance data");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchSearchMetrics();
    setRefreshing(false);
  };

  const formatTime = (ms: number) => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatPercentage = (value: number) => `${value.toFixed(1)}%`;

  if (!selectedSite) {
    return (
      <div className="container mx-auto p-6">
        <Alert>
          <AlertDescription>
            Please select a site to view search performance metrics.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const strategyData = metrics?.searchesByStrategy
    ? Object.entries(metrics.searchesByStrategy).map(([key, value]) => ({
        name: key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
        value,
      }))
    : [];

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Search Performance</h1>
          <p className="text-muted-foreground mt-1">
            Monitor natural language search performance and optimization metrics
          </p>
        </div>
        <div className="flex gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1h">Last Hour</SelectItem>
              <SelectItem value="24h">Last 24 Hours</SelectItem>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="90d">Last 90 Days</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total Searches</CardTitle>
            <Search className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.totalSearches || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics?.zeroResultSearches || 0} with no results
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Avg Response Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatTime(metrics?.avgSearchTime || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              P90: {formatTime(metrics?.p90SearchTime || 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Avg Results</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(metrics?.avgResults || 0).toFixed(1)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Per search query
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Performance</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatTime(metrics?.medianSearchTime || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Median response time
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Search Volume Over Time */}
        <Card>
          <CardHeader>
            <CardTitle>Search Volume</CardTitle>
            <CardDescription>
              Number of searches over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="searches"
                  stroke={COLORS.primary}
                  name="Searches"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Response Time Trends */}
        <Card>
          <CardHeader>
            <CardTitle>Response Time Trends</CardTitle>
            <CardDescription>
              Average and P90 response times
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip formatter={(value: number) => formatTime(value)} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="avgTime"
                  stroke={COLORS.secondary}
                  name="Average"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="p90Time"
                  stroke={COLORS.tertiary}
                  name="P90"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Search Strategy Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Search Strategies</CardTitle>
            <CardDescription>
              Distribution of search approaches
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={strategyData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  <Cell fill={COLORS.primary} />
                  <Cell fill={COLORS.secondary} />
                  <Cell fill={COLORS.tertiary} />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Timing Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Performance Breakdown</CardTitle>
            <CardDescription>
              Time spent in each phase
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={timingBreakdown} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="phase" type="category" />
                <Tooltip formatter={(value: number) => formatTime(value)} />
                <Bar dataKey="avgTime" fill={COLORS.primary} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Popular Searches */}
        <Card>
          <CardHeader>
            <CardTitle>Popular Searches</CardTitle>
            <CardDescription>
              Most frequent search queries
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {popularSearches.slice(0, 5).map((search, index) => (
                <div key={index} className="flex justify-between items-center">
                  <div className="flex-1">
                    <p className="text-sm font-medium truncate">{search.query}</p>
                    <p className="text-xs text-muted-foreground">
                      {search.count} searches • {formatTime(search.avgTime)}
                    </p>
                  </div>
                  <Badge variant="secondary">{search.avgResults} results</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cache Performance */}
      <Card>
        <CardHeader>
          <CardTitle>Cache Performance</CardTitle>
          <CardDescription>
            Cache hit rate over time
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={hourlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip formatter={(value: number) => formatPercentage(value)} />
              <Legend />
              <Line
                type="monotone"
                dataKey="cacheHitRate"
                stroke={COLORS.secondary}
                name="Cache Hit Rate %"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}